import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "./Select";

export type SortDir = "asc" | "desc";
export type SortState<F extends string = string> = { field: F; dir: SortDir };

/** "tag,asc" — the `sort` query parameter Spring Data understands. */
export function sortParam(s: SortState): string {
  return `${s.field},${s.dir}`;
}

/** Same column again flips the direction; a new column starts ascending. */
export function nextSort<F extends string>(current: SortState<F>, field: F): SortState<F> {
  if (current.field === field) return { field, dir: current.dir === "asc" ? "desc" : "asc" };
  return { field, dir: "asc" };
}

/**
 * Column header that sorts on click (server-side sort — the page sends `sort=field,dir`).
 * Shows ▲ / ▼ on the active column and sets `aria-sort` for screen readers.
 */
export function SortableTh<F extends string>({ field, sort, onSort, children, className }: {
  field: F;
  sort: SortState<F>;
  onSort: (next: SortState<F>) => void;
  children: ReactNode;
  /** Passed to the `<th>` — e.g. `hide-tablet` for a secondary column. */
  className?: string;
}) {
  const { t } = useTranslation();
  const active = sort.field === field;
  return (
    <th className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`th-sort${active ? " is-active" : ""}`}
        onClick={() => onSort(nextSort(sort, field))}
        title={t("table.sortBy", "Sortiraj po ovoj koloni")}
      >
        <span>{children}</span>
        <span className="th-sort-arrow" aria-hidden="true">{active ? (sort.dir === "asc" ? "▲" : "▼") : "△"}</span>
      </button>
    </th>
  );
}

/**
 * The same server-side sort for a card list on a phone (≤820px), where the table header is
 * hidden (hard rule 12: nothing only for the wide layout): one searchable list with every column
 * in both directions. Hidden above 820px — the headers do the job there.
 */
export function SortSelect<F extends string>({ fields, sort, onSort }: {
  fields: { field: F; label: string }[];
  sort: SortState<F>;
  onSort: (next: SortState<F>) => void;
}) {
  const { t } = useTranslation();
  const options = fields.flatMap(({ field, label }) => [
    { value: sortParam({ field, dir: "asc" }), label: t("table.sortOption", "Sortiraj: {{label}} {{arrow}}", { label, arrow: "▲" }) },
    { value: sortParam({ field, dir: "desc" }), label: t("table.sortOption", "Sortiraj: {{label}} {{arrow}}", { label, arrow: "▼" }) },
  ]);
  return (
    <div className="sort-phone">
      <Select<string>
        value={sortParam(sort)}
        options={options}
        onChange={(v) => {
          const [field, dir] = v.split(",");
          onSort({ field: field as F, dir: dir === "desc" ? "desc" : "asc" });
        }}
        ariaLabel={t("table.sortLabel", "Sortiranje")}
      />
    </div>
  );
}
